import { useCallback } from 'react';
import type { FieldPath, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ParcelCreateFormValues } from './useParcelCreateFormSchema';

/**
 * Resolve a mensagem de erro de um campo do formulário de parcela.
 *
 * As mensagens do schema são **chaves de i18n**, não texto final: o schema é
 * derivado do gerado pelo Kubb e não tem acesso ao `t()`. Sem esta tradução,
 * reprovar um campo não produziria sinal legível — o `handleSubmit` barraria o
 * envio e o botão pareceria inerte.
 *
 * @param form - Formulário da tela de criação de parcela.
 * @returns Função que devolve a mensagem traduzida do campo, ou `undefined`.
 *
 * @example
 * ```tsx
 * const errorOf = useParcelFormError(form);
 * <TextField hasError={errorOf('total') !== undefined} helper={errorOf('total')} />
 * ```
 */
export function useParcelFormError(
  form: UseFormReturn<ParcelCreateFormValues>,
): (name: FieldPath<ParcelCreateFormValues>) => string | undefined {
  const { t } = useTranslation('individualPayment');
  const { formState, getFieldState } = form;

  return useCallback(
    (name: FieldPath<ParcelCreateFormValues>) => {
      const message = getFieldState(name, formState).error?.message;
      // biome-ignore lint/suspicious/noExplicitAny: a chave vem do schema, fora do union tipado de t().
      return typeof message === 'string' ? String(t(message as any)) : undefined;
    },
    [t, formState, getFieldState],
  );
}
